/**
 * salesSummary.ts — Pure aggregation of a date range's outward activity into
 * the figures the Sales report shows.
 *
 * Two rules this file exists to enforce, both of which the old in-screen
 * aggregation got wrong:
 *
 *  1. **Everything is net of returns.** A credit note reduces revenue, tax, and
 *     the product's sold quantity. Previously credit notes were counted and then
 *     discarded, so a shop that sold ₹1,00,000 and refunded ₹20,000 reported
 *     ₹1,00,000.
 *  2. **Refunds are signed, not summed.** `useCreateCreditNote` records a refund
 *     as a *positive* `payments` row against the CREDIT_NOTE invoice. Summing
 *     payments by method therefore *adds* refunds to cash taken. The sign comes
 *     from the parent invoice's type.
 *
 * Pure — no DB access — so the netting rules are unit-testable. See
 * `salesDataSource.ts` for the WatermelonDB bridge.
 */

export const CREDIT_NOTE = 'CREDIT_NOTE';
export const SALE = 'SALE';

/** +1 for a sale, −1 for a credit note. */
export function signOf(invoiceType: string): 1 | -1 {
  return invoiceType === CREDIT_NOTE ? -1 : 1;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface SalesInvoiceInput {
  type: string;
  totalAmount: number;
  totalTax: number;
  dueAmount: number;
  createdAt: Date;
}

export interface SalesPaymentInput {
  method: string;
  amount: number;
  /** Type of the invoice this payment belongs to — drives the sign. */
  invoiceType: string;
}

export interface SalesLineInput {
  productId: string;
  quantity: number;
  totalAmount: number;
  invoiceType: string;
}

export interface SalesSummaryInput {
  /** SALE + CREDIT_NOTE for the range. Voided invoices must already be excluded. */
  invoices: SalesInvoiceInput[];
  /** Payments *received* in the range — not payments belonging to invoices raised in it. */
  payments: SalesPaymentInput[];
  lines: SalesLineInput[];
  productNames: Map<string, string>;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface TopProduct {
  name: string;
  quantity: number;
  revenue: number;
}

export interface SalesSummary {
  /** Sale invoices before returns. */
  grossSales: number;
  /** Total credit-note value in the range. */
  refunds: number;
  /** grossSales − refunds. The number that means "revenue". */
  totalSales: number;
  /** Net of credit-note tax — matches the GSTR-3B outward figure. */
  totalTax: number;
  invoiceCount: number;
  creditNoteCount: number;
  /** Mean *sale* invoice value; unaffected by returns. */
  avgSaleValue: number;
  cashSales: number;
  upiSales: number;
  cardSales: number;
  otherSales: number;
  creditDue: number;
  /** Net quantity and revenue per product; returned units subtract. */
  topProducts: TopProduct[];
  /** Gross sale value by hour of day — a traffic curve, deliberately not netted. */
  hourly: number[];
}

const KNOWN_METHODS = ['CASH', 'UPI', 'CARD'];

export function buildSalesSummary(input: SalesSummaryInput): SalesSummary {
  const { invoices, payments, lines, productNames } = input;

  const sales = invoices.filter((i) => i.type !== CREDIT_NOTE);
  const credits = invoices.filter((i) => i.type === CREDIT_NOTE);

  const grossSales = sales.reduce((s, i) => s + i.totalAmount, 0);
  const refunds = credits.reduce((s, i) => s + i.totalAmount, 0);
  const grossTax = sales.reduce((s, i) => s + i.totalTax, 0);
  const refundTax = credits.reduce((s, i) => s + i.totalTax, 0);

  // Payments: a refund is a positive row on a CREDIT_NOTE, so subtract it.
  const paySum = (predicate: (method: string) => boolean) =>
    payments
      .filter((p) => predicate(p.method))
      .reduce((s, p) => s + signOf(p.invoiceType) * p.amount, 0);

  // Product roll-up, net of returned units.
  const productMap = new Map<string, { qty: number; rev: number }>();
  for (const li of lines) {
    const sign = signOf(li.invoiceType);
    const acc = productMap.get(li.productId) ?? { qty: 0, rev: 0 };
    acc.qty += sign * li.quantity;
    acc.rev += sign * li.totalAmount;
    productMap.set(li.productId, acc);
  }

  const topProducts = [...productMap.entries()]
    .map(([id, v]) => ({
      name: productNames.get(id) ?? 'Unknown',
      quantity: v.qty,
      revenue: v.rev,
    }))
    // A product whose only activity was a return nets to zero — drop it rather
    // than show a 0-qty row.
    .filter((p) => p.quantity !== 0 || p.revenue !== 0)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  const hourly = Array.from({ length: 24 }, (_, h) =>
    sales
      .filter((inv) => inv.createdAt.getHours() === h)
      .reduce((s, inv) => s + inv.totalAmount, 0),
  );

  return {
    grossSales,
    refunds,
    totalSales: grossSales - refunds,
    totalTax: grossTax - refundTax,
    invoiceCount: sales.length,
    creditNoteCount: credits.length,
    avgSaleValue: sales.length ? grossSales / sales.length : 0,
    cashSales: paySum((m) => m === 'CASH'),
    upiSales: paySum((m) => m === 'UPI'),
    cardSales: paySum((m) => m === 'CARD'),
    otherSales: paySum((m) => !KNOWN_METHODS.includes(m)),
    creditDue: sales.reduce((s, inv) => s + inv.dueAmount, 0),
    topProducts,
    hourly,
  };
}
